import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import './page-Productos/d-productos.css';
import './page-Productos/m-productos.css';
// The product card visuals are shared with the home page grid.
import './productos-destacados/d-productosdestacados.css';
import './productos-destacados/m-productosdestacados.css';
import PriceFilter from './Left-column/PriceFilter';
import Categorias from './Left-column/Categorias';
import MdcOffer from './banners/longbanners/MdcOffer';
import Postnav from './Postnav/Postnav';
import OrdenarPor from './main/OrdenarPor';
import { FiltrosProductosContext } from '../Hooks/useFetchFiltrosProductos';

const TODAS = null;

/** Only leaf categories are accepted by the avisos endpoint, so a parent
 *  selection has to fan out into every leaf beneath it. */
const hojasDe = (filtros, raiz) => {
  const hijosDe = (id) => filtros.filter((f) => f.idfiltropadre === id);
  const raices = raiz === TODAS
    ? filtros.filter((f) => f.idfiltropadre === null)
    : filtros.filter((f) => f.idfiltro === raiz);

  const hojas = [];
  const visitar = (filtro) => {
    const hijos = hijosDe(filtro.idfiltro);
    if (hijos.length === 0) hojas.push(filtro.idfiltro);
    else hijos.forEach(visitar);
  };
  raices.forEach(visitar);
  return [...new Set(hojas)];
};

const Productos = () => {
  const { filtros } = useContext(FiltrosProductosContext) || {};
  const [categoria, setCategoria] = useState(TODAS);
  const [rango, setRango] = useState({ min: '', max: '' });
  const [orden, setOrden] = useState('relevante');
  const [avisos, setAvisos] = useState([]);
  const [estado, setEstado] = useState('cargando');
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    if (!filtros) return undefined;

    let cancelado = false;
    const ids = hojasDe(filtros, categoria);

    if (ids.length === 0) {
      setAvisos([]);
      setEstado('listo');
      return undefined;
    }

    setEstado('cargando');

    // One request per leaf category: the API has no "all avisos" endpoint.
    Promise.allSettled(
      ids.map((id) => fetch(`/api/avisos-por-filtro/${id}`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })),
    ).then((resultados) => {
      if (cancelado) return;

      const cumplidos = resultados.filter((r) => r.status === 'fulfilled');
      if (cumplidos.length === 0) {
        setEstado('error');
        return;
      }

      const porId = new Map();
      cumplidos.forEach(({ value }) => {
        (Array.isArray(value) ? value : []).forEach((a) => porId.set(a.Idaviso, a));
      });

      setAvisos([...porId.values()]);
      setEstado('listo');
    });

    return () => { cancelado = true; };
  }, [filtros, categoria, intento]);

  const visibles = useMemo(() => {
    const min = rango.min === '' ? -Infinity : Number(rango.min);
    const max = rango.max === '' ? Infinity : Number(rango.max);

    const enRango = avisos.filter((a) => {
      const precio = Number(a.Precio);
      return Number.isFinite(precio) ? precio >= min && precio <= max : true;
    });

    if (orden === 'menor-precio') return [...enRango].sort((a, b) => a.Precio - b.Precio);
    if (orden === 'mayor-precio') return [...enRango].sort((a, b) => b.Precio - a.Precio);
    return enRango;
  }, [avisos, rango, orden]);

  const limpiar = useCallback(() => {
    setCategoria(TODAS);
    setRango({ min: '', max: '' });
    setOrden('relevante');
  }, []);

  const hayFiltros = categoria !== TODAS || rango.min !== '' || rango.max !== '';

  return (
    <div className="outheader">
      <div className="background">
        <div className="background-center-color">
          <div className="postnav-container">
            <Postnav />
          </div>

          <div className="maincenterbackground productos-page">
            <div className="longbanners">
              <MdcOffer type="Offer" />
            </div>

            <main className="productos-layout">
              <aside className="productos-sidebar">
                <Categorias
                  filtros={filtros}
                  seleccionada={categoria}
                  onSeleccionar={setCategoria}
                />
                <PriceFilter rango={rango} onCambiar={setRango} />
              </aside>

              <section className="productos-main">
                <header className="productos-toolbar">
                  <p className="productos-conteo">
                    {estado === 'listo'
                      ? `${visibles.length} ${visibles.length === 1 ? 'producto' : 'productos'}`
                      : ''}
                  </p>
                  <div className="productos-toolbar-acciones">
                    {hayFiltros && (
                      <button type="button" className="productos-limpiar" onClick={limpiar}>
                        Limpiar filtros
                      </button>
                    )}
                    <OrdenarPor valor={orden} onOrdenar={setOrden} />
                  </div>
                </header>

                {estado === 'cargando' && (
                  <div className="productos-grid" aria-busy="true">
                    {Array.from({ length: 8 }, (_, i) => (
                      <div key={i} className="producto-skeleton" />
                    ))}
                  </div>
                )}

                {estado === 'error' && (
                  <div className="productos-aviso">
                    <h3>No pudimos cargar los productos.</h3>
                    <p>Revisá tu conexión y volvé a intentar.</p>
                    <button type="button" onClick={() => setIntento((n) => n + 1)}>
                      Reintentar
                    </button>
                  </div>
                )}

                {estado === 'listo' && visibles.length === 0 && (
                  <div className="productos-aviso">
                    <h3>No hay productos con estos filtros.</h3>
                    <p>Probá ampliar el rango de precio o elegir otra categoría.</p>
                    {hayFiltros && (
                      <button type="button" onClick={limpiar}>Ver todos</button>
                    )}
                  </div>
                )}

                {estado === 'listo' && visibles.length > 0 && (
                  <div className="productos-grid">
                    {visibles.map((producto) => (
                      <a
                        key={producto.Idaviso}
                        className="recuadro"
                        href={`/aviso/${producto.Idaviso}`}
                      >
                        <div className="d-recuadrocontent">
                          <div className="recuadro-media">
                            <img src={producto.Imagen1} alt={producto.Titulo} loading="lazy" />
                          </div>
                          <div className="recuadro-body">
                            <div className="recuadro-titulo">
                              <p>{producto.Titulo}</p>
                            </div>
                            <div className="recuadro-precio">
                              <h3>${producto.Precio}</h3>
                            </div>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </section>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Productos;
